import type { BattleRuntimeState } from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";

export const SANDBOX_RARITY_IDS = [1, 2, 3, 4];

const COMMISSION_RARITY_WEIGHTS: Array<Record<number, number>> = [
  { 1: 1000, 2: 0, 3: 0, 4: 0 },
  { 1: 900, 2: 100, 3: 0, 4: 0 },
  { 1: 780, 2: 200, 3: 20, 4: 0 },
  { 1: 650, 2: 300, 3: 40, 4: 10 },
  { 1: 500, 2: 400, 3: 80, 4: 20 },
  { 1: 300, 2: 400, 3: 160, 4: 40 },
];

export function getEffectiveRarityWeights(state?: BattleRuntimeState, commissionIndexOverride?: number): number[] {
  const commissionIndex = Math.max(
    1,
    Math.min(COMMISSION_RARITY_WEIGHTS.length, commissionIndexOverride || state?.commissionIndex || 1),
  );
  const baseWeights = COMMISSION_RARITY_WEIGHTS[commissionIndex - 1] ?? COMMISSION_RARITY_WEIGHTS[0];
  return SANDBOX_RARITY_IDS.map((rarity) => {
    const multiplier = state?.rarityWeightMultipliers?.[rarity] ?? 1;
    return Math.max(0, (baseWeights[rarity] ?? 0) * multiplier);
  });
}

export function getRarityPercentages(state?: BattleRuntimeState, commissionIndexOverride?: number): number[] {
  const weighted = getEffectiveRarityWeights(state, commissionIndexOverride);
  const total = weighted.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return SANDBOX_RARITY_IDS.map(() => 0);
  return weighted.map((value) => value / total * 100);
}

export function pickWeightedUniqueGraphs(
  items: PropRuntimeGraph[],
  count: number,
  state?: BattleRuntimeState,
  commissionIndexOverride?: number,
): PropRuntimeGraph[] {
  const pool = [...items];
  const result: PropRuntimeGraph[] = [];
  while (pool.length && result.length < count) {
    const picked = pickOneWeightedGraph(pool, state, commissionIndexOverride);
    if (!picked) break;
    result.push(picked);
  }
  return result;
}

export function pickRefreshedChoiceGraphs(
  items: PropRuntimeGraph[],
  count: number,
  state?: BattleRuntimeState,
  commissionIndexOverride?: number,
  options?: { forcedThirdGraphId?: string },
): PropRuntimeGraph[] {
  const pool = [...items];
  const result: PropRuntimeGraph[] = [];
  const forcedThirdIndex = options?.forcedThirdGraphId
    ? pool.findIndex((item) => item._id === options.forcedThirdGraphId)
    : -1;
  const forcedThird = forcedThirdIndex >= 0 ? pool.splice(forcedThirdIndex, 1)[0] : undefined;

  while (pool.length && result.length < Math.min(2, count)) {
    const picked = pickOneWeightedGraph(pool, state, commissionIndexOverride);
    if (!picked) break;
    result.push(picked);
  }

  if (pool.length && result.length < count) {
    if (forcedThird) {
      result.push(forcedThird);
    } else {
      const picked = pickOneWeightedGraph(
        pool,
        state,
        commissionIndexOverride,
        { zeroCommonWeight: true, fallbackRarityWhenNoWeight: 2 },
      )
        ?? pickOneWeightedGraph(pool, state, commissionIndexOverride);
      if (picked) result.push(picked);
    }
  } else if (forcedThird && result.length < count) {
    result.push(forcedThird);
  }

  while (pool.length && result.length < count) {
    const picked = pickOneWeightedGraph(pool, state, commissionIndexOverride);
    if (!picked) break;
    result.push(picked);
  }

  return result;
}

function pickOneWeightedGraph(
  pool: PropRuntimeGraph[],
  state?: BattleRuntimeState,
  commissionIndexOverride?: number,
  options?: { zeroCommonWeight?: boolean; fallbackRarityWhenNoWeight?: number },
): PropRuntimeGraph | undefined {
  if (!pool.length) return undefined;
  const rarity = pickAvailableRarity(pool, state, commissionIndexOverride, options);
  const rarityPool = pool
    .map((item, index) => ({ item, index }))
    .filter((entry) => entry.item.rarity === rarity);
  const sourcePool = rarityPool.length ? rarityPool : pool.map((item, index) => ({ item, index }));
  const picked = sourcePool[Math.floor(Math.random() * sourcePool.length)];
  const [item] = pool.splice(picked.index, 1);
  return item;
}

function pickAvailableRarity(
  pool: PropRuntimeGraph[],
  state?: BattleRuntimeState,
  commissionIndexOverride?: number,
  options?: { zeroCommonWeight?: boolean; fallbackRarityWhenNoWeight?: number },
): number {
  const weights = getEffectiveRarityWeights(state, commissionIndexOverride)
    .map((weight, index) => (options?.zeroCommonWeight && SANDBOX_RARITY_IDS[index] === 1 ? 0 : weight));
  const available = SANDBOX_RARITY_IDS
    .map((rarity, index) => ({
      rarity,
      weight: pool.some((item) => item.rarity === rarity) ? weights[index] : 0,
    }))
    .filter((item) => item.weight > 0);
  if (!available.length && typeof options?.fallbackRarityWhenNoWeight === "number") {
    const fallbackRarity = options.fallbackRarityWhenNoWeight;
    if (pool.some((item) => item.rarity === fallbackRarity)) return fallbackRarity;
  }
  const weightedPool = available.length
    ? available
    : SANDBOX_RARITY_IDS
      .filter((rarity) => pool.some((item) => item.rarity === rarity))
      .map((rarity) => ({ rarity, weight: 1 }));
  const total = weightedPool.reduce((sum, item) => sum + item.weight, 0);
  let roll = Math.random() * total;
  for (const item of weightedPool) {
    roll -= item.weight;
    if (roll <= 0) return item.rarity;
  }
  return weightedPool[weightedPool.length - 1]?.rarity ?? pool[0]?.rarity ?? 1;
}
